import type { Translations } from "./en";

// Ukrainian plural: one (1, 21, 31…), few (2–4, 22–24…), many (0, 5–20, 25–30…).
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

const tries = (n: number) => plural(n, "спроба", "спроби", "спроб");

export const uk: Translations = {
  common: {
    loading: "Завантаження…",
    back: "Назад",
    cancel: "Скасувати",
    delete: "Видалити",
    close: "Закрити",
    download: "Завантажити",
    edit: "Редагувати",
    original: "Оригінал",
    noImageAvailable: "Зображення недоступне",
    zoomHint: "Зведіть пальці для масштабування • Подвійний дотик — скинути",
    deleteImageTitle: "Видалити зображення?",
    deleteVersionMessage:
      "Буде видалено цю версію редагування. Оригінал та інші версії залишаться.",
    deletePermanentMessage:
      "Зображення та всю історію його редагувань буде видалено назавжди.",
  },
  colorNames: {
    Black: "Чорний",
    White: "Білий",
    Red: "Червоний",
    Emerald: "Смарагдовий",
    Navy: "Темно-синій",
    "Blush Pink": "Пудрово-рожевий",
    Lavender: "Лавандовий",
    Champagne: "Шампань",
  },
  gate: {
    title: "Вхід для адміністратора",
    subtitle: "Введіть пароль, щоб відкрити цю сторінку.",
    passwordPlaceholder: "Пароль",
    submit: "Увійти",
    checking: "Перевіряємо…",
    wrongPassword: "Неправильний пароль. Спробуйте ще раз.",
  },
  header: {
    openCreatePanel: "Відкрити панель створення",
  },
  sidebar: {
    title: "Створити зображення",
    subtitle: "Генерація за допомогою ШІ",
    tryOnLinks: "Примірочна",
    prompt: "Промпт",
    promptPlaceholder: "Опишіть зображення, яке хочете згенерувати…",
    referenceImage: "Референсні зображення (необов’язково)",
    referenceAlt: (n) => `Референс ${n}`,
    addMore: (n, max) => `Додати ще (${n}/${max})`,
    dropOrBrowse: "Перетягніть зображення або торкніться, щоб вибрати",
    aspectRatio: "Співвідношення сторін",
    model: "Модель",
    modelFast: "Швидка",
    modelHighQuality: "Висока якість",
    generating: "Генерується…",
    generateImage: "Згенерувати зображення",
  },
  grid: {
    noImagesTitle: "Ще немає зображень",
    noImagesHint: "Введіть промпт на панелі, щоб згенерувати перше зображення",
  },
  card: {
    generationFailed: "Не вдалося згенерувати",
    waitingToStart: "Очікує початку…",
    generating: "Генерується…",
    deleteConfirm: "Видалити це зображення?",
  },
  imageModal: {
    title: "Деталі зображення",
    editHistory: "Історія редагувань",
    editApplied: "Застосоване редагування",
    prompt: "Промпт",
    referenceImage: "Референсне зображення",
    referenceImages: (n) => `Референсні зображення (${n})`,
  },
  editModal: {
    title: "Редагування зображення",
    editHistory: "Історія редагувань",
    appliedEdit: "Застосоване редагування",
    originalPrompt: "Початковий промпт",
    describeYourEdit: "Опишіть ваше редагування",
    editPlaceholder: "Напр., зробити небо фіолетовим, додати веселку…",
    applyingEdit: "Застосовуємо редагування…",
    applyEdit: "Застосувати",
  },
  admin: {
    backToGenerator: "Назад до генератора",
    title: "Посилання для примірки",
    subtitle:
      "Створіть посилання, надішліть клієнтці — її примірки з’являться тут.",
    generationPrompt: "Промпт генерації",
    generationPromptNote:
      "використовується для кожної примірки; інструкція щодо кольору додається автоматично",
    saved: "Збережено ✓",
    savePrompt: "Зберегти промпт",
    resetToDefault: "Скинути до стандартного",
    availableColors: "Доступні кольори сукні",
    colorsShown: (n, total) => `${n} з ${total} показано на сторінках примірки`,
    colorsNote:
      "Клієнти завжди бачать «Як є»; вимкнені кольори одразу зникають з їхньої сторінки.",
    loadingSessions: "Завантажуємо сеанси…",
    sessionsHeading: (n) => `Сеанси (${n})`,
    nobodyOpened: "Це посилання ще ніхто не відкривав.",
    unknownDevice: "Невідомий пристрій",
    otherDevice: "Інший",
    firstSeen: (date) => `вперше ${date}`,
    lastActive: (date) => `остання активність ${date}`,
    triesUsed: (n) => `${n} ${tries(n)}`,
    consentedAt: (date) => `Згоду надано ${date}`,
    noConsentRecorded: "Згоду не зафіксовано",
    consentYes: "згода ✓",
    consentNo: "згода —",
    generatedImages: (n) => `Згенеровані зображення (${n})`,
    changeTryLimit: "Змінити ліміт спроб",
    triesNoLimit: (used) => `${used} ${tries(used)} · без ліміту`,
    triesOf: (used, max) => `${used} / ${max} спроб`,
    titlePlaceholder: "Назва, напр. «Олена — весняна колекція»",
    triesLabel: "Спроби",
    creating: "Створюємо…",
    generateLink: "Створити посилання",
    preselectedDressAlt: "Попередньо обрана сукня",
    removeDress: "Прибрати сукню",
    preselectDress:
      "+ Обрати сукню заздалегідь (необов’язково) — клієнтка зможе її замінити",
    noLinksYet: "Ще немає посилань. Створіть перше вище.",
    paused: "призупинено",
    sessionCount: (n) => `${n} ${plural(n, "сеанс", "сеанси", "сеансів")}`,
    createdAt: (date) => `Створено ${date}`,
    noTryOnsYet: "ще немає примірок",
    tryOnsCompleted: (completed, total) =>
      `${completed}/${total} примірок завершено`,
    lastActivity: (date) => `остання активність ${date}`,
    colorsTried: (colors) => `Спробувані кольори: ${colors}`,
    generationCost: (amount) => `вартість генерацій ≈ ${amount}`,
    deleteLink: "Видалити",
    deleteLinkTitle: "Видалити це посилання?",
    deleteLinkMessage: (title) =>
      `Це назавжди видалить «${title}» — посилання, всі згенеровані зображення, фото клієнтів та історію сеансів. Цю дію неможливо скасувати.`,
    deletingLink: "Видаляємо…",
    pause: "Призупинити",
    resume: "Відновити",
    copied: "Скопійовано ✓",
    copyLink: "Копіювати посилання",
    hideDetails: "Сховати деталі",
    sessionsAndImages: "Сеанси та зображення",
    recentTryOnAlt: "Нещодавня примірка",
    tryOnAlt: "Примірка",
  },
  tryon: {
    consentTitle: "Перш ніж почати",
    consentBody:
      "Щоб показати сукню на вас, завантажені фото обробляються ШІ-сервісом і зберігаються, щоб ви та бутик, який надіслав вам це посилання, могли бачити результати. Більше вони ні для чого не використовуються — жодної реклами, жодного навчання ШІ, і їх видаляють на ваш запит.",
    consentAgreePrefix:
      "Я погоджуюся на обробку моїх фотографій для створення зображень віртуальної примірки, як описано в документі ",
    termsNoticeLink: "Умови використання та повідомлення про конфіденційність",
    agreeContinue: "Погоджуюсь і продовжую",
    tapToReplace: "Торкніться, щоб замінити",
    ourPick: "Наш вибір — торкніться, щоб змінити",
    yourPhoto: "Ваше фото",
    yourPhotoHint: "Найкраще — фото на повний зріст",
    theDress: "Сукня",
    theDressHint: "Фото або скриншот",
    errorNoTries: "Ви використали всі спроби за цим посиланням.",
    errorLinkPaused: "Це посилання призупинено.",
    errorGeneric: "Щось пішло не так. Спробуйте ще раз.",
    linkNotFoundTitle: "Такого посилання не існує",
    linkNotFoundHint: "Перевірте адресу або попросіть нове посилання.",
    linkPausedTitle: "Це посилання призупинено",
    linkPausedHint:
      "Зверніться до людини, яка надіслала його вам, щоб знову його ввімкнути.",
    subtitle: (needsDress) =>
      `Додайте своє фото${needsDress ? " і сукню" : ""} — ми покажемо сукню на вас. Це займе близько 20 секунд.`,
    noTriesLeft: "Спроб не залишилося",
    triesLeft: (left, max) => `Залишилося ${left} з ${max} спроб`,
    dressColor: "Колір сукні",
    asIs: "Як є",
    uploading: "Завантажуємо…",
    generating: "Створюємо…",
    go: "Приміряти ✨",
    duplicateWarning:
      "Ви вже створили образ для цього фото й кольору — він у розділі «Ваші образи» нижче.",
    generateAnyway: "Все одно створити",
    usedAllTries:
      "Ви використали всі спроби. Попросіть більше в людини, яка надіслала вам посилання.",
    creatingLook: "Створюємо ваш образ — зазвичай це займає ~20 секунд…",
    yourLooks: "Ваші образи",
    tryOnResultAlt: "Результат примірки",
    resultFailed: "Цей образ не вдався — спробуйте ще раз.",
    resultColor: (name) => `Колір: ${name}`,
    footerNote:
      "Ваші фото використовуються лише для створення зображень примірки.",
    termsShort: "Умови та конфіденційність",
  },
};
